import { c as _c } from "react/compiler-runtime";
import { GetAvailableVariables } from '@/api/generated/model';
import { EAggregatedDataType } from '@/api/requests/types/charts.type';
import { tokenizeRawExpression, tokensToMathJsExpression } from '@/components/ui/configuration/variables-select-parser';
import { formatDuration } from '@/utils/format.util';
import { useProjectsContext } from '@/providers/projects-provider';
import { parse, type MathNode } from 'mathjs';
import { useCallback, useMemo } from 'react';
import { useAvailableVariablesApiV1ReportsProjectIdAvailableVariablesGet as useAvailableVariables } from '@/api/generated/reports-available/reports-available';
type UnitMap = Record<string, number>;
type UnitResolution = {
  status: 'ok';
  unit: UnitMap | null;
} | {
  status: 'invalid';
  unit: null;
};
const UNIT_CACHE = new Map<string, UnitResolution>();
const BASE_UNITS_BY_SLUG: Record<string, UnitMap> = {
  [EAggregatedDataType.CONVERSATION_TIME]: {
    time: 1
  },
  [EAggregatedDataType.LATENCY]: {
    time: 1
  },
  [EAggregatedDataType.USERS]: {
    users: 1
  }
};
const PERCENTAGE_UNIT_SLUGS = new Set<string>([EAggregatedDataType.PERCENTAGE, EAggregatedDataType.ERROR_RATE, EAggregatedDataType.ERROR_RATE_EXPLICIT, EAggregatedDataType.BOUNCE_RATE, EAggregatedDataType.RETURN_RATE, EAggregatedDataType.PENETRATION, EAggregatedDataType.SATISFACTION, EAggregatedDataType.FRUSTRATION]);
export function resolveUnitForCustomVariable(variable: GetAvailableVariables, availableVariablesBySlug: Map<string, GetAvailableVariables>): UnitResolution {
  const cacheKey = getUnitCacheKey(variable);
  const cached = UNIT_CACHE.get(cacheKey);
  if (cached) return cached;
  const resolution = resolveUnitForFormula(variable.raw_formula ?? '', availableVariablesBySlug, new Set([variable.slug]));
  UNIT_CACHE.set(cacheKey, resolution);
  return resolution;
}
function resolveUnitForFormula(rawFormula: string, availableVariablesBySlug: Map<string, GetAvailableVariables>, visited: Set<string>): UnitResolution {
  let expression: string;
  try {
    const tokens = tokenizeRawExpression(rawFormula);
    expression = tokensToMathJsExpression(tokens);
  } catch {
    return {
      status: 'invalid',
      unit: null
    };
  }
  let ast: MathNode;
  try {
    ast = parse(expression);
  } catch {
    return {
      status: 'invalid',
      unit: null
    };
  }
  const unit = deriveUnitFromNode(ast, availableVariablesBySlug, visited);
  if (!unit) return {
    status: 'invalid',
    unit: null
  };
  return {
    status: 'ok',
    unit
  };
}
function deriveUnitFromNode(node: MathNode, availableVariablesBySlug: Map<string, GetAvailableVariables>, visited: Set<string>): UnitMap | null {
  const nodeType = (node as {
    type?: string;
  }).type;
  if (nodeType === 'ParenthesisNode' && 'content' in node) {
    const content = (node as unknown as {
      content: MathNode;
    }).content;
    return deriveUnitFromNode(content, availableVariablesBySlug, visited);
  }
  if (nodeType === 'ConstantNode') {
    return {};
  }
  if (nodeType === 'SymbolNode') {
    const name = (node as unknown as {
      name: string;
    }).name;
    return resolveUnitForSlug(name, availableVariablesBySlug, visited);
  }
  if (nodeType === 'OperatorNode') {
    const opNode = node as unknown as {
      op: string;
      args: MathNode[];
    };
    const {
      op,
      args
    } = opNode;
    if (!args || args.length === 0) return {};
    if (op === '+' || op === '-') {
      if (args.length === 1) return deriveUnitFromNode(args[0], availableVariablesBySlug, visited);
      const units = args.map(arg => deriveUnitFromNode(arg, availableVariablesBySlug, visited));
      if (units.some(unit => unit === null)) return null;
      return mergeAdditiveUnits(units as UnitMap[]);
    }
    if (op === '*' || op === '/') {
      const first = deriveUnitFromNode(args[0], availableVariablesBySlug, visited);
      if (!first) return null;
      return args.slice(1).reduce((acc, arg) => {
        if (!acc) return null;
        const next = deriveUnitFromNode(arg, availableVariablesBySlug, visited);
        if (!next) return null;
        return op === '*' ? combineUnits(acc, next, 1) : combineUnits(acc, next, -1);
      }, first as UnitMap | null);
    }
    return null;
  }
  return null;
}
function resolveUnitForSlug(slug: string, availableVariablesBySlug: Map<string, GetAvailableVariables>, visited: Set<string>): UnitMap | null {
  const variable = availableVariablesBySlug.get(slug);
  if (!variable) return null;
  if (variable.is_custom && variable.raw_formula) {
    const cached = UNIT_CACHE.get(getUnitCacheKey(variable));
    if (cached) return cached.status === 'ok' ? cached.unit : null;
  } else {
    const cached = UNIT_CACHE.get(slug);
    if (cached) return cached.status === 'ok' ? cached.unit : null;
  }
  if (visited.has(slug)) return null;
  if (BASE_UNITS_BY_SLUG[slug]) return {
    ...BASE_UNITS_BY_SLUG[slug]
  };
  if (PERCENTAGE_UNIT_SLUGS.has(slug)) return {
    percentage: 1
  };
  if (variable.is_custom && variable.raw_formula) {
    visited.add(slug);
    const resolution = resolveUnitForFormula(variable.raw_formula, availableVariablesBySlug, visited);
    visited.delete(slug);
    UNIT_CACHE.set(getUnitCacheKey(variable), resolution);
    return resolution.status === 'ok' ? resolution.unit : null;
  }
  return {
    [slug]: 1
  };
}
function getUnitCacheKey(variable: GetAvailableVariables): string {
  return `${variable.slug}:${variable.raw_formula ?? ''}`;
}
function combineUnits(base: UnitMap, other: UnitMap, sign: 1 | -1): UnitMap {
  const out: UnitMap = {
    ...base
  };
  for (const key of Object.keys(other)) {
    out[key] = (out[key] ?? 0) + sign * other[key];
    if (out[key] === 0) delete out[key];
  }
  return out;
}
function unitsEqual(a: UnitMap, b: UnitMap): boolean {
  const aKeys = Object.keys(a).sort();
  const bKeys = Object.keys(b).sort();
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key, index) => key === bKeys[index] && a[key] === b[key]);
}
function mergeAdditiveUnits(units: UnitMap[]): UnitMap | null {
  const nonEmpty = units.filter(unit => Object.keys(unit).length > 0);
  if (nonEmpty.length === 0) return {};
  const base = nonEmpty[0];
  for (const unit of nonEmpty.slice(1)) {
    if (!unitsEqual(base, unit)) return null;
  }
  return base;
}
export function getTimeUnitLabel(seconds: number): string | null {
  const converted = formatDuration(seconds, {
    inputUnit: 'seconds',
    style: 'human'
  });
  if (!converted) return null;
  return converted.replace(/^[\\d.\\s]+/, '').trim();
}
export function buildUnitSuffix(unit: UnitMap, options: {
  timeLabel: string | null;
  includeTime: boolean;
}): string | null {
  const numerator: string[] = [];
  const denominator: string[] = [];
  for (const [key, exp] of Object.entries(unit)) {
    if (!exp) continue;
    if (key === 'time' && !options.includeTime) continue;
    const label = key === 'time' ? options.timeLabel || 'sec' : key === 'users' ? 'users' : key;
    const abs = Math.abs(exp);
    const token = abs === 1 ? label : `${label}^${abs}`;
    if (exp > 0) numerator.push(token);else denominator.push(token);
  }
  if (numerator.length === 0 && denominator.length === 0) return null;
  if (denominator.length === 0) return `${numerator.join(' * ')}`;
  const num = numerator.length === 0 ? '1' : numerator.join(' * ');
  const den = denominator.join(' * ');
  return `${num} / ${den}`;
}
export function hasCustomVariableUnitSuffix(variableSlug: string, availableVariablesBySlug: Map<string, GetAvailableVariables>): boolean {
  const variable = availableVariablesBySlug.get(variableSlug);
  if (!variable?.is_custom || !variable.raw_formula) return false;
  const resolution = resolveUnitForCustomVariable(variable, availableVariablesBySlug);
  if (resolution.status !== 'ok' || !resolution.unit) return false;
  const unitKeys = Object.keys(resolution.unit);
  return unitKeys.length > 0 && unitKeys.every(key => key === 'time' || key === 'users');
}

/**
 * True when a custom variable is a plain count of users (unit `users^1`), i.e. its formatted value
 * would be rendered with a trailing "users" word. Callers can use this to show a users icon instead.
 */
export function isCustomVariableUsersUnit(variableSlug: string, availableVariablesBySlug: Map<string, GetAvailableVariables>): boolean {
  const variable = availableVariablesBySlug.get(variableSlug);
  if (!variable?.is_custom || !variable.raw_formula) return false;
  const resolution = resolveUnitForCustomVariable(variable, availableVariablesBySlug);
  if (resolution.status !== 'ok' || !resolution.unit) return false;
  const activeUnits = Object.entries(resolution.unit).filter(([, exp]) => exp);
  return activeUnits.length === 1 && activeUnits[0][0] === 'users' && activeUnits[0][1] === 1;
}
export function resolveCustomVariableLabel(variableSlug: string, availableVariablesBySlug: Map<string, GetAvailableVariables>, fallback: string): string {
  const variable = availableVariablesBySlug.get(variableSlug);
  if (variable?.is_custom && variable.name) return String(variable.name);
  return fallback;
}
export function useResolveCustomVariableLabel(t0, projectId) {
  const $ = _c(8);
  const reportType = t0 === undefined ? "analytics" : t0;
  const {
    selectedProject
  } = useProjectsContext();
  const projectIdForVariables = projectId ?? selectedProject?.id;
  let t1;
  if ($[0] !== reportType) {
    t1 = {
      report_type: reportType
    };
    $[0] = reportType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !!projectIdForVariables;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      query: {
        enabled: t2
      }
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data: availableVariablesData
  } = useAvailableVariables(projectIdForVariables ?? "", t1, t3);
  availableVariablesData?.variables;
  let t4;
  if ($[4] !== availableVariablesData?.variables) {
    t4 = new Map((availableVariablesData?.variables ?? []).map(_temp));
    $[4] = availableVariablesData?.variables;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const availableVariablesBySlug = t4;
  let t5;
  if ($[6] !== availableVariablesBySlug) {
    t5 = value => {
      const resolvedLabel = resolveCustomVariableLabel(value, availableVariablesBySlug, value);
      return resolvedLabel ?? value;
    };
    $[6] = availableVariablesBySlug;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
function _temp(variable) {
  return [variable.slug, variable];
}
export function resolveCustomVariableDescription(variableSlug: string, availableVariablesBySlug: Map<string, GetAvailableVariables>, fallback: string): string {
  const variable = availableVariablesBySlug.get(variableSlug);
  if (variable?.is_custom && variable.description) return String(variable.description);
  return fallback;
}
export function useResolveCustomVariableDescription(t0, projectId) {
  const $ = _c(8);
  const reportType = t0 === undefined ? "analytics" : t0;
  const {
    selectedProject
  } = useProjectsContext();
  const projectIdForVariables = projectId ?? selectedProject?.id;
  let t1;
  if ($[0] !== reportType) {
    t1 = {
      report_type: reportType
    };
    $[0] = reportType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !!projectIdForVariables;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      query: {
        enabled: t2
      }
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data: availableVariablesData
  } = useAvailableVariables(projectIdForVariables ?? "", t1, t3);
  availableVariablesData?.variables;
  let t4;
  if ($[4] !== availableVariablesData?.variables) {
    t4 = new Map((availableVariablesData?.variables ?? []).map(_temp2));
    $[4] = availableVariablesData?.variables;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const availableVariablesBySlug = t4;
  let t5;
  if ($[6] !== availableVariablesBySlug) {
    t5 = value => {
      const resolvedLabel = resolveCustomVariableDescription(value, availableVariablesBySlug, value);
      return resolvedLabel ?? value;
    };
    $[6] = availableVariablesBySlug;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
function _temp2(variable) {
  return [variable.slug, variable];
}